import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "./CookieChecker";
import TreeReportsRequest from "../Endpoints/TreeReportsRequest";

// Master IB is NOT a role/type - the login response and the "type" cookie
// never return "MASTER_IB". Whether a user is a Master IB is a per-tree
// fact, discovered by calling GET /tree-reports/trees and checking each
// tree's `is_master` flag, e.g.:
// { "status": "Success", "data": { "email": "...", "trees": [{ "id": 1015, "is_master": true, ... }] } }
//
// This lives in its own provider/context, completely separate from
// CookieChecker on purpose:
//   - CookieChecker only ever validates cookies/session and must stay 100%
//     free of network calls, so a failure here can never interrupt login.
//   - MasterIbProvider is mounted from NavbarAndSidebar, i.e. only AFTER
//     CookieChecker has already confirmed a valid session and rendered its
//     children.
// Any failure here (network error, CORS, 4xx/5xx, unexpected shape, etc.)
// only ever falls back to "not a Master IB" - it must never touch
// cookies/session or trigger a navigation.
const MasterIbContext = createContext({
  isMasterIb: false,
  masterTrees: [],
  checkingMasterIb: false,
});

export const useMasterIb = () => useContext(MasterIbContext);

export const MasterIbProvider = ({ children }) => {
  const cookiesContext = useCookies() || {};
  const { type, token } = cookiesContext;

  const [isMasterIb, setIsMasterIb] = useState(false);
  const [masterTrees, setMasterTrees] = useState([]);
  const [checkingMasterIb, setCheckingMasterIb] = useState(false);
  const lookupDoneForToken = useRef(null);

  useEffect(() => {
    const isAdminType = type === "ADMIN" || type === "SUPERADMIN";

    if (!token || !type || isAdminType) {
      setIsMasterIb(false);
      setMasterTrees([]);
      return undefined;
    }

    // Only fetch once per token (login), not on every render/navigation.
    if (lookupDoneForToken.current === token) return undefined;
    lookupDoneForToken.current = token;

    let alive = true;
    setCheckingMasterIb(true);

    TreeReportsRequest()
      .then((response) => {
        if (!alive) return;

        const trees = Array.isArray(response?.data?.data?.trees)
          ? response.data.data.trees
          : [];
        const ownedAsMaster = trees.filter((tree) => Boolean(tree?.is_master));

        setMasterTrees(ownedAsMaster);
        setIsMasterIb(ownedAsMaster.length > 0);
      })
      .catch((requestError) => {
        // Best-effort only - never touch cookies/session/navigation here.
        if (!alive) return;
        console.error("Unable to load tree ownership info (Master IB check):", requestError);
        setMasterTrees([]);
        setIsMasterIb(false);
        lookupDoneForToken.current = null; // allow retrying later
      })
      .finally(() => {
        if (alive) setCheckingMasterIb(false);
      });

    return () => {
      alive = false;
    };
  }, [token, type]);

  return (
    <MasterIbContext.Provider value={{ isMasterIb, masterTrees, checkingMasterIb }}>
      {children}
    </MasterIbContext.Provider>
  );
};

export default MasterIbProvider;